import { db } from '../config/firebase';
import {
  collection, doc, addDoc, getDoc, getDocs, updateDoc,
  serverTimestamp, writeBatch, increment, arrayUnion
} from 'firebase/firestore';
import { Chess } from 'chess.js';
import {
  generateSwissPairings, generateKnockoutPairings, computeStandings,
} from './swissPairing';

/** Create a new round: writes the round doc, one pairing doc per match,
 * and a live chessGames doc for every non-bye pairing. Byes are awarded
 * immediately (no game needed). */
export async function createRound({ tournamentId, roundNumber, phase, pairings, clockSeconds }) {
  const batch = writeBatch(db);
  const roundRef = doc(collection(db, 'tournaments', tournamentId, 'rounds'));

  batch.set(roundRef, {
    roundNumber,
    phase,
    status: 'in_progress',
    createdAt: serverTimestamp(),
  });

  const startFen = new Chess().fen();

  for (const p of pairings) {
    const pairingRef = doc(collection(db, 'tournaments', tournamentId, 'rounds', roundRef.id, 'pairings'));

    if (p.isBye) {
      batch.set(pairingRef, {
        ...p,
        gameId: null,
        result: 'player1',
        completedAt: serverTimestamp(),
      });
      const partRef = doc(db, 'tournaments', tournamentId, 'participants', p.player1Id);
      batch.update(partRef, { score: increment(1), byes: increment(1) });
      continue;
    }

    const gameRef = doc(collection(db, 'chessGames'));
    batch.set(gameRef, {
      isPrivate: true,
      isTournament: true,
      tournamentId,
      roundId: roundRef.id,
      pairingId: pairingRef.id,
      status: 'active',
      fen: startFen,
      turn: 'w',
      white: { id: p.player1Id, name: p.player1Name, rating: p.player1Rating || 1200 },
      black: { id: p.player2Id, name: p.player2Name, rating: p.player2Rating || 1200 },
      winner: null,
      resultReason: null,
      whiteTimeLeft: clockSeconds,
      blackTimeLeft: clockSeconds,
      turnStartedAt: serverTimestamp(),
      lastFrom: null,
      lastTo: null,
      moveHistory: [],
      createdAt: serverTimestamp(),
    });

    batch.set(pairingRef, {
      ...p,
      gameId: gameRef.id,
      result: null,
      completedAt: null,
    });
  }

  batch.update(doc(db, 'tournaments', tournamentId), {
    currentRound: roundNumber,
    currentPhase: phase,
    status: phase === 'knockout' ? 'knockout' : 'swiss',
  });

  await batch.commit();
  return roundRef.id;
}

/** Generate the next Swiss round from current standings. */
export async function generateNextSwissRound(tournamentId, clockSeconds) {
  const partSnap = await getDocs(collection(db, 'tournaments', tournamentId, 'participants'));
  const participants = partSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  const pairings = generateSwissPairings(participants);

  const tSnap = await getDoc(doc(db, 'tournaments', tournamentId));
  const nextRound = (tSnap.data()?.currentRound || 0) + 1;

  return createRound({ tournamentId, roundNumber: nextRound, phase: 'swiss', pairings, clockSeconds });
}

/** Transition from Swiss into the knockout bracket using current standings. */
export async function startKnockout(tournamentId, bracketSize, clockSeconds) {
  const partSnap = await getDocs(collection(db, 'tournaments', tournamentId, 'participants'));
  const participants = partSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  const standings = computeStandings(participants).slice(0, bracketSize);
  const pairings = generateKnockoutPairings(standings);

  return createRound({ tournamentId, roundNumber: 1, phase: 'knockout', pairings, clockSeconds });
}

/** Generate the next knockout round from the winners of the previous one. */
export async function generateNextKnockoutRound(tournamentId, previousRoundId, previousRoundNumber) {
  const pairingsSnap = await getDocs(collection(db, 'tournaments', tournamentId, 'rounds', previousRoundId, 'pairings'));
  const winners = [];
  pairingsSnap.docs.forEach(d => {
    const p = d.data();
    if (p.result === 'player1') winners.push({ id: p.player1Id, name: p.player1Name, rating: p.player1Rating });
    else if (p.result === 'player2') winners.push({ id: p.player2Id, name: p.player2Name, rating: p.player2Rating });
    // draws are handled via a manual replay trigger and won't have a result yet
  });

  if (winners.length === 1) {
    const tRef = doc(db, 'tournaments', tournamentId);
    const tData = (await getDoc(tRef)).data() || {};
    await updateDoc(tRef, {
      status: 'finished',
      winnerId: winners[0].id,
      winnerName: winners[0].name,
      finishedAt: serverTimestamp(),
    });

    // ── Champion enters the HALL OF FAME (tournament champions ONLY —
    // this is the permanent record, written the moment a title is won).
    try {
      await addDoc(collection(db, 'chessHallOfFame'), {
        championId: winners[0].id,
        championName: winners[0].name,
        tournamentId,
        tournamentName: tData.name || 'Tournament',
        photoURL: null, // champion can add their portrait from the Hall
        wonAt: serverTimestamp(),
      });
    } catch (e) { console.error('hall of fame', e); }

    // ── COINS: every participant earns coins from their score (score×10),
    // the champion gets a +100 bonus on top. Coins are later exchanged
    // for real prizes by Iyanna Foundation.
    try {
      const partSnap = await getDocs(collection(db, 'tournaments', tournamentId, 'participants'));
      const batch = writeBatch(db);
      partSnap.docs.forEach(d => {
        const p = d.data();
        const earned = Math.round((p.score || 0) * 10) + (d.id === winners[0].id ? 100 : 0);
        if (earned > 0) {
          batch.set(doc(db, 'chessPlayers', d.id), { coins: increment(earned) }, { merge: true });
          batch.update(doc(db, 'tournaments', tournamentId, 'participants', d.id), { coinsEarned: earned });
        }
      });
      await batch.commit();
    } catch (e) { console.error('coins', e); }

    return null;
  }

  const pairings = generateKnockoutPairings(winners);
  const tSnap = await getDoc(doc(db, 'tournaments', tournamentId));
  const clockSeconds = tSnap.data()?.clockSeconds || 900;

  return createRound({
    tournamentId,
    roundNumber: previousRoundNumber + 1,
    phase: 'knockout',
    pairings,
    clockSeconds,
  });
}

/** Create a rematch for a drawn knockout pairing (colors reversed). Used
 * as the tiebreak method since knockout can't end in a draw. */
export async function replayDrawnPairing(tournamentId, roundId, pairingId, clockSeconds) {
  const pairingRef = doc(db, 'tournaments', tournamentId, 'rounds', roundId, 'pairings', pairingId);
  const snap = await getDoc(pairingRef);
  if (!snap.exists()) return;
  const p = snap.data();

  const startFen = new Chess().fen();
  const gameRef = await addDoc(collection(db, 'chessGames'), {
    isPrivate: true,
    isTournament: true,
    tournamentId,
    roundId,
    pairingId,
    status: 'active',
    fen: startFen,
    turn: 'w',
    white: { id: p.player2Id, name: p.player2Name, rating: p.player2Rating || 1200 },
    black: { id: p.player1Id, name: p.player1Name, rating: p.player1Rating || 1200 },
    winner: null,
    resultReason: null,
    whiteTimeLeft: clockSeconds,
    blackTimeLeft: clockSeconds,
    turnStartedAt: serverTimestamp(),
    lastFrom: null,
    lastTo: null,
    moveHistory: [],
    isReplay: true,
    createdAt: serverTimestamp(),
  });

  await updateDoc(pairingRef, { gameId: gameRef.id, result: null, completedAt: null, isReplay: true });
}

/** Called when a tournament game finishes — records the result into the
 * pairing doc and updates both participants' Swiss score/history. */
export async function recordTournamentGameResult(gameData) {
  const { tournamentId, roundId, pairingId, winner, isTournament } = gameData;
  if (!isTournament || !tournamentId || !roundId || !pairingId) return;

  const pairingRef = doc(db, 'tournaments', tournamentId, 'rounds', roundId, 'pairings', pairingId);
  const pairingSnap = await getDoc(pairingRef);
  if (!pairingSnap.exists()) return;
  const pairing = pairingSnap.data();
  const tSnap = await getDoc(doc(db, 'tournaments', tournamentId));
  const phase = tSnap.data()?.currentPhase || 'swiss';

  if (winner === 'draw' && phase === 'knockout') {
    await updateDoc(pairingRef, { result: 'draw_pending_replay' });
    return;
  }

  const resultKey = winner === 'white' ? 'player1' : winner === 'black' ? 'player2' : 'draw';
  await updateDoc(pairingRef, { result: resultKey, completedAt: serverTimestamp() });

  if (phase === 'swiss') {
    const whiteScore = winner === 'white' ? 1 : winner === 'draw' ? 0.5 : 0;
    const blackScore = winner === 'black' ? 1 : winner === 'draw' ? 0.5 : 0;

    const whiteRef = doc(db, 'tournaments', tournamentId, 'participants', pairing.player1Id);
    const blackRef = doc(db, 'tournaments', tournamentId, 'participants', pairing.player2Id);

    await Promise.all([
      updateDoc(whiteRef, {
        score: increment(whiteScore),
        opponents: arrayUnion(pairing.player2Id),
        'colorHistory.w': increment(1),
      }),
      updateDoc(blackRef, {
        score: increment(blackScore),
        opponents: arrayUnion(pairing.player1Id),
        'colorHistory.b': increment(1),
      }),
    ]);
  }
}

/** Are all pairings in a round finished (or awaiting a replay)? */
export async function isRoundComplete(tournamentId, roundId) {
  const snap = await getDocs(collection(db, 'tournaments', tournamentId, 'rounds', roundId, 'pairings'));
  return snap.docs.every(d => {
    const p = d.data();
    return p.isBye || (p.result && p.result !== 'draw_pending_replay');
  });
}
